/**
 * API Client - Refactored with Resource Factory
 * 
 * Centralized API layer with authentication and impersonation support.
 * Uses createApiResource for standard CRUD patterns.
 */
import { useAuthStore } from './authStore';

const API_URL = '/api';

// ============= Core Fetch Layer =============

/**
 * Generic API fetch with auth and impersonation support
 */
export const apiFetch = async (endpoint, options = {}) => {
    const { token } = useAuthStore.getState();

    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers
    };

    // Global Context Injection (Impersonation Support)
    let finalEndpoint = `${API_URL}${endpoint}`;
    const urlParams = new URLSearchParams(window.location.search);
    const businessId = urlParams.get('business_id');

    if (businessId && !finalEndpoint.includes('business_id=')) {
        const separator = finalEndpoint.includes('?') ? '&' : '?';
        finalEndpoint = `${finalEndpoint}${separator}business_id=${businessId}`;
    }

    const response = await fetch(finalEndpoint, { ...options, headers });

    if (response.status === 401) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        throw new Error('Authentication required');
    }

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'API request failed');
    return data;
};

// ============= Resource Factory =============

/**
 * Create standard CRUD API resource
 */
const createResource = (name, customMethods = {}) => ({
    getAll: (params) => apiFetch(`/${name}` + (params ? '?' + new URLSearchParams(params) : '')),
    getOne: (id) => apiFetch(`/${name}/${id}`),
    create: (data) => apiFetch(`/${name}`, { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiFetch(`/${name}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiFetch(`/${name}/${id}`, { method: 'DELETE' }),
    ...customMethods
});

// ============= Standard CRUD Resources =============

export const contactsApi = {
    ...createResource('contacts'),
    get360: (id) => apiFetch(`/contacts/${id}/360`),
    merge: (keepId, mergeId) => apiFetch('/contacts/merge', { method: 'POST', body: JSON.stringify({ keepId, mergeId }) }),
};
export const opportunitiesApi = createResource('opportunities');
export const bookingsApi = createResource('bookings');
export const campaignsApi = {
    ...createResource('campaigns'),
    sendCampaign: (id) => apiFetch(`/campaigns/${id}/send`, { method: 'POST' }),
    getLimit: () => apiFetch('/campaigns/limit')
};
export const invoicesApi = {
    ...createResource('invoices'),
    prefillFromBooking: (bookingId) => apiFetch(`/invoices/prefill/${bookingId}`),
    getUnlinkedBookings: () => apiFetch('/invoices/unlinked-bookings'),
    downloadPdf: (id) => {
        // PDF needs responseType: blob — must use fetch directly with impersonation support
        const { token } = useAuthStore.getState();
        const urlParams = new URLSearchParams(window.location.search);
        const businessId = urlParams.get('business_id');
        let url = `${API_URL}/invoices/${id}/pdf`;
        if (businessId) url += `?business_id=${businessId}`;
        return fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    },
    send: (id) => apiFetch(`/invoices/${id}/send`, { method: 'POST' }),
    sendPaymentLink: (id, email) => apiFetch(`/invoices/${id}/send-payment-link`, { method: 'POST', body: JSON.stringify({ email }) }),
};
export const usersApi = createResource('users');
export const blockedTimeApi = {
    getRange: (start, end) => apiFetch(`/blocked-time?start=${start}&end=${end}`),
    create: (data) => apiFetch('/blocked-time', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiFetch(`/blocked-time/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiFetch(`/blocked-time/${id}`, { method: 'DELETE' })
};
export const servicesApi = createResource('services');

// ============= Custom Resources =============

export const configApi = {
    get: async () => (await fetch('/api/config')).json()
};

export const callsApi = {
    getAll: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.type) qs.set('type', params.type);
        if (params.offset) qs.set('offset', params.offset);
        if (params.limit) qs.set('limit', params.limit);
        const query = qs.toString();
        return apiFetch(`/calls${query ? `?${query}` : ''}`);
    },
    getPage: (offset, limit = 50) => apiFetch(`/calls?offset=${offset}&limit=${limit}`),
    getSummary: () => apiFetch('/calls/summary'),
    create: (data) => apiFetch('/calls', { method: 'POST', body: JSON.stringify(data) })
};

export const businessesApi = {
    getAll: () => apiFetch('/businesses'),
    getOne: (id) => apiFetch(`/businesses/${id}`),
    getFeatures: (id) => apiFetch(`/businesses/${id}/features`),
    create: (data) => apiFetch('/businesses', { method: 'POST', body: JSON.stringify(data) }),
    updateSettings: (id, data) => apiFetch(`/businesses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiFetch(`/businesses/${id}`, { method: 'DELETE' }),
    goLive: (id, data) => apiFetch(`/businesses/${id}/go-live`, { method: 'POST', body: JSON.stringify(data) }),
};

export const statsApi = {
    getDashboard: () => apiFetch('/stats/dashboard')
};

export const vapiApi = {
    getCalls: () => apiFetch('/vapi/calls'),
    verify: (data) => apiFetch('/vapi/verify', { method: 'POST', body: JSON.stringify(data) }),
    sync: () => apiFetch('/vapi/sync', { method: 'POST' }),
    getStatus: () => apiFetch('/vapi/status')
};

export const vapiManagementApi = {
    scrapeWebsite: (url, businessId) => apiFetch(`/vapi-management/scrape-website${businessId ? `?business_id=${businessId}` : ''}`, {
        method: 'POST', body: JSON.stringify({ url })
    }),
    onboard: (config, businessId) => apiFetch(`/vapi-management/onboard${businessId ? `?business_id=${businessId}` : ''}`, {
        method: 'POST', body: JSON.stringify(config)
    }),
    getFiles: (businessId) => apiFetch(`/vapi-management/files${businessId ? `?business_id=${businessId}` : ''}`),
};

export const usageApi = {
    getStats: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return apiFetch(`/stats/usage${params.toString() ? '?' + params : ''}`);
    },
    getSummary: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return apiFetch(`/stats/usage/summary${params.toString() ? '?' + params : ''}`);
    },
    getExportUrl: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return `/api/stats/usage/export${params.toString() ? '?' + params : ''}`;
    },
    getDaily: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return apiFetch(`/stats/usage/daily${params.toString() ? '?' + params : ''}`);
    },
    getHistory: () => apiFetch('/stats/usage/history'),
    getTwilioCosts: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return apiFetch(`/stats/twilio${params.toString() ? '?' + params : ''}`);
    },
    getWhatsApp: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return apiFetch(`/stats/usage/whatsapp${params.toString() ? '?' + params : ''}`);
    },
    getBusinessUsage: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return apiFetch(`/stats/usage/business${params.toString() ? '?' + params : ''}`);
    },
    getAdminOverview: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return apiFetch(`/stats/usage/admin-overview${params.toString() ? '?' + params : ''}`);
    },
    getAdminExportUrl: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return `/api/stats/usage/admin-export${params.toString() ? '?' + params : ''}`;
    },
    getBreakdown: (month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return apiFetch(`/stats/usage/breakdown${params.toString() ? '?' + params : ''}`);
    },
    trackEvent: (event, properties = {}) => {
        // Fire-and-forget — don't block UI
        apiFetch('/stats/usage/track', {
            method: 'POST',
            body: JSON.stringify({ event, properties })
        }).catch(() => { });
    }
};

export const stripeApi = {
    createCheckout: (data) => apiFetch('/stripe/checkout', { method: 'POST', body: JSON.stringify(data) }),
    createPaymentRequest: (data) => apiFetch('/stripe/payment-request', { method: 'POST', body: JSON.stringify(data) })
};

export const stripeConnectApi = {
    getConfig: () => apiFetch('/stripe/connect/config'),
    createAccount: () => apiFetch('/stripe/connect/create', { method: 'POST' }),
    createAccountSession: () => apiFetch('/stripe/connect/account-session', { method: 'POST' }),
    getStatus: () => apiFetch('/stripe/connect/status'),
    disconnect: () => apiFetch('/stripe/connect/disconnect', { method: 'POST' }),
};

export const notificationsApi = {
    get: (limit = 20) => apiFetch(`/stats/notifications?limit=${limit}`),
    getCount: () => apiFetch('/stats/notifications/count'),
    markRead: (id) => apiFetch(`/stats/notifications/${id}/read`, { method: 'POST' }),
    markAllRead: () => apiFetch('/stats/notifications/read-all', { method: 'POST' }),
    dismiss: (id) => apiFetch(`/stats/notifications/${id}/dismiss`, { method: 'POST' }),
    checkThresholds: () => apiFetch('/stats/notifications/check', { method: 'POST' }),
};

export const messagesApi = {
    getAll: (params) => apiFetch('/messages' + (params ? '?' + new URLSearchParams(params) : '')),
    send: (data) => apiFetch('/messages/send', { method: 'POST', body: JSON.stringify(data) }),
    sendWithAttachments: async (formData) => {
        const { token } = useAuthStore.getState();
        const urlParams = new URLSearchParams(window.location.search);
        const businessId = urlParams.get('business_id');
        let url = `/api/messages/send-with-attachments`;
        if (businessId) url += `?business_id=${businessId}`;
        const res = await fetch(url, {
            method: 'POST',
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: formData  // No Content-Type — browser sets multipart boundary
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Send failed');
        return data;
    },
    getAttachments: (messageId) => apiFetch(`/messages/${messageId}/attachments`),
    downloadAttachmentUrl: (messageId, attachmentId) => {
        const urlParams = new URLSearchParams(window.location.search);
        const businessId = urlParams.get('business_id');
        let url = `/api/messages/${messageId}/attachments/${attachmentId}`;
        if (businessId) url += `?business_id=${businessId}`;
        return url;
    },
    downloadEmlUrl: (messageId) => {
        const urlParams = new URLSearchParams(window.location.search);
        const businessId = urlParams.get('business_id');
        let url = `/api/messages/${messageId}/export.eml`;
        if (businessId) url += `?business_id=${businessId}`;
        return url;
    },
    markRead: (messageId) => apiFetch(`/messages/${messageId}/read`, { method: 'PATCH' }),
    markUnread: (messageId) => apiFetch(`/messages/${messageId}/unread`, { method: 'PATCH' }),
    markThreadRead: (contactId) => apiFetch(`/messages/contact/${contactId}/read-all`, { method: 'PATCH' }),
    markThreadUnread: (contactId) => apiFetch(`/messages/contact/${contactId}/unread-all`, { method: 'PATCH' }),
    markSessionThreadRead: (sessionId) => apiFetch(`/messages/session/${sessionId}/read-all`, { method: 'PATCH' }),
    deleteThread: (contactId, subject) => apiFetch(`/messages/contact/${contactId}/thread${subject ? '?subject=' + encodeURIComponent(subject) : ''}`, { method: 'DELETE' }),
    restoreThread: (contactId, subject) => apiFetch(`/messages/contact/${contactId}/thread/restore${subject ? '?subject=' + encodeURIComponent(subject) : ''}`, { method: 'PUT' }),
    permanentDeleteThread: (contactId, subject) => apiFetch(`/messages/contact/${contactId}/thread/permanent${subject ? '?subject=' + encodeURIComponent(subject) : ''}`, { method: 'DELETE' }),
    deleteSessionThread: (sessionId) => apiFetch(`/messages/session/${sessionId}/thread`, { method: 'DELETE' }),
    getUnreadCount: () => apiFetch('/messages/unread-count'),
    // Drafts
    listDrafts: () => apiFetch('/messages/drafts'),
    saveDraft: (data) => apiFetch('/messages/drafts', { method: 'POST', body: JSON.stringify(data) }),
    deleteDraft: (id) => apiFetch(`/messages/drafts/${id}`, { method: 'DELETE' }),
    // Retry failed
    retryFailed: (messageId) => apiFetch(`/messages/${messageId}/retry`, { method: 'POST' }),
    // Star / Archive / Bulk
    toggleStar: (messageId) => apiFetch(`/messages/${messageId}/star`, { method: 'PATCH' }),
    archiveThread: (contactId) => apiFetch(`/messages/threads/${contactId}/archive`, { method: 'PATCH' }),
    unarchiveThread: (contactId) => apiFetch(`/messages/threads/${contactId}/unarchive`, { method: 'PATCH' }),
    bulkAction: (contactIds, action) => apiFetch('/messages/bulk-action', { method: 'POST', body: JSON.stringify({ contactIds, action }) }),
    // Templates
    listTemplates: () => apiFetch('/messages/templates'),
    createTemplate: (data) => apiFetch('/messages/templates', { method: 'POST', body: JSON.stringify(data) }),
    deleteTemplate: (id) => apiFetch(`/messages/templates/${id}`, { method: 'DELETE' }),
    // Identities
    // Identities & Rules
    getIdentities: () => apiFetch('/messages/identities'),
    createIdentity: (data) => apiFetch('/messages/identities', { method: 'POST', body: JSON.stringify(data) }),
    updateIdentity: (id, data) => apiFetch(`/messages/identities/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteIdentity: (id) => apiFetch(`/messages/identities/${id}`, { method: 'DELETE' }),
    getRules: () => apiFetch('/messages/rules'),
    createRule: (data) => apiFetch('/messages/rules', { method: 'POST', body: JSON.stringify(data) }),
    deleteRule: (id) => apiFetch(`/messages/rules/${id}`, { method: 'DELETE' }),
    // Scheduling & Snooze
    scheduleSend: (data) => apiFetch('/messages/scheduled', { method: 'POST', body: JSON.stringify(data) }),
    listScheduled: () => apiFetch('/messages/scheduled'),
    cancelScheduled: (id) => apiFetch(`/messages/scheduled/${id}`, { method: 'DELETE' }),
    snoozeThread: (contactId, snoozed_until) => apiFetch(`/messages/threads/${contactId}/snooze`, { method: 'PATCH', body: JSON.stringify({ snoozed_until }) }),
    getSessionThread: (sessionId) => apiFetch(`/messages/session/${sessionId}`),
    webchatTakeover: (sessionId, active) => apiFetch('/messages/webchat/takeover', {
        method: 'POST', body: JSON.stringify({ sessionId, active })
    }),
    webchatReply: (sessionId, content) => apiFetch('/messages/webchat/reply', {
        method: 'POST', body: JSON.stringify({ sessionId, content })
    }),
    searchContact: (fields) => apiFetch('/messages/webchat/search-contact', {
        method: 'POST', body: JSON.stringify(fields)
    }),
    linkWebchatContact: (sessionId, contactId) => apiFetch('/messages/webchat/link-contact', {
        method: 'POST', body: JSON.stringify({ sessionId, contactId })
    }),
    summariseThread: (contactId) => apiFetch(`/messages/threads/${contactId}/summarise`),
    suggestReplies: (contactId) => apiFetch(`/messages/threads/${contactId}/suggest-replies`)
};

export const smsApi = {
    send: (data) => apiFetch('/sms/send', { method: 'POST', body: JSON.stringify(data) }),
    sendCampaign: (id) => apiFetch(`/campaigns/${id}/send`, { method: 'POST' })
};

export const subscriptionApi = {
    getStatus: () => apiFetch('/subscription/status'),
    createSession: (planType) => apiFetch('/subscription/create-session', {
        method: 'POST',
        body: JSON.stringify({ planType })
    }),
    upgrade: (planType) => apiFetch('/subscription/upgrade', {
        method: 'POST',
        body: JSON.stringify({ planType })
    }),
    createPortalSession: () => apiFetch('/subscription/portal-session', {
        method: 'POST'
    })
};

export const authApi = {
    twoFactor: {
        getStatus: () => apiFetch('/auth/2fa/status'),
        setupTotp: () => apiFetch('/auth/2fa/setup/totp', { method: 'POST' }),
        verifyTotp: (code) => apiFetch('/auth/2fa/verify/totp', { method: 'POST', body: JSON.stringify({ code }) }),
        setupEmail: () => apiFetch('/auth/2fa/setup/email', { method: 'POST' }),
        disable: (password) => apiFetch('/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ password }) }),
    },
    changePassword: (data) => apiFetch('/auth/change-password', { method: 'POST', body: JSON.stringify(data) }),
    updateProfile: (data) => apiFetch('/users/profile', { method: 'PUT', body: JSON.stringify(data) }),
};

export const adminApi = {
    getStats: () => apiFetch('/platform/admin/stats'),
    getConfig: () => apiFetch('/platform/config'),
    getBusinessSummary: (id, month, year) => {
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (year) params.set('year', year);
        return apiFetch(`/platform/admin/business/${id}/summary${params.toString() ? '?' + params : ''}`);
    },
    getInvoices: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.business_id) params.set('business_id', filters.business_id);
        if (filters.status) params.set('status', filters.status);
        if (filters.limit) params.set('limit', filters.limit);
        if (filters.offset) params.set('offset', filters.offset);
        return apiFetch(`/platform/admin/invoices${params.toString() ? '?' + params : ''}`);
    },
    createInvoice: (data) => apiFetch('/platform/admin/invoices', { method: 'POST', body: JSON.stringify(data) }),
    deleteInvoice: (id) => apiFetch(`/platform/admin/invoices/${id}`, { method: 'DELETE' }),
    updateInvoice: (id, data) => apiFetch(`/platform/admin/invoices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    getSubscriptions: () => apiFetch('/platform/admin/subscriptions'),
    // Generic helpers for admin-scoped endpoints
    get: (path) => apiFetch(path),
    post: (path, data) => apiFetch(path, { method: 'POST', body: JSON.stringify(data) }),
    patch: (path, data) => apiFetch(path, { method: 'PATCH', body: JSON.stringify(data) }),
};

export const googleApi = {
    getAuthUrl: () => apiFetch('/google/auth/url'),
    getStatus: () => apiFetch('/google/status'),
    disconnect: () => apiFetch('/google/disconnect', { method: 'POST' }),
    retryDiscovery: () => apiFetch('/google/retry-discovery', { method: 'POST' }),
    getReviews: () => apiFetch('/google/reviews'),
    searchPlaces: (query) => apiFetch(`/google/place-search?query=${encodeURIComponent(query)}`),
    savePlaceId: (placeId, placeName) => apiFetch('/google/save-place-id', { method: 'POST', body: JSON.stringify({ placeId, placeName }) }),
    postReply: (reviewId, reply) => apiFetch(`/google/reviews/${reviewId}/reply`, { method: 'POST', body: JSON.stringify({ reply }) }),
    markReplied: (reviewId, replyContent) => apiFetch(`/google/reviews/${reviewId}/mark-replied`, { method: 'POST', body: JSON.stringify({ replyContent }) }),
    aiSuggest: (reviewId) => apiFetch(`/google/reviews/${reviewId}/ai-suggest`, { method: 'POST' }),
    bulkReply: () => apiFetch('/google/reviews/bulk-reply', { method: 'POST' }),
    getAutoReplySettings: () => apiFetch('/google/reviews/auto-reply/settings'),
    saveAutoReplySettings: (settings) => apiFetch('/google/reviews/auto-reply/settings', { method: 'POST', body: JSON.stringify(settings) }),
};

export const twilioApi = {
    verify: (data) => apiFetch('/twilio/verify', { method: 'POST', body: JSON.stringify(data) }),
};

export const emailIdentitiesApi = {
    getAll: () => apiFetch('/email-identities'),
    create: (data) => apiFetch('/email-identities', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiFetch(`/email-identities/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiFetch(`/email-identities/${id}`, { method: 'DELETE' }),
    setDefault: (id) => apiFetch(`/email-identities/${id}/set-default`, { method: 'POST' }),
    testSmtp: (data) => apiFetch('/email-identities/test-smtp', { method: 'POST', body: JSON.stringify(data) }),
    testImap: (data) => apiFetch('/email-identities/test-imap', { method: 'POST', body: JSON.stringify(data) }),
};

export const emailApi = {
    testConnection: (data) => apiFetch('/email/test-connection', { method: 'POST', body: JSON.stringify(data) }),
};

export const voiceApi = {
    getToken: () => apiFetch('/voice/token', { method: 'POST' }),
    getStatus: () => apiFetch('/voice/status'),
};
